import { assert, subTestContext, Test } from "oc-e2e-testrunner";
import { v2 } from "oc-threejs/short3";
import { resetArrangement, assertWarnungen } from "./utils";
import {
  erzeugeSchiebetorRahmen,
  schiebetorKennzahlen,
  schiebetorMasse,
} from "../arrangement3d/schiebetor/erzeugeSchiebetorRahmen";

/**
 * Testet die Breiten-/Maßberechnung des Schiebetores (schiebetorKennzahlen / schiebetorMasse /
 * erzeugeSchiebetorRahmen) sowie den Öffnungsplatzbedarf inkl. SchiebetorPuffer und die
 * Fächer-/Füllungsfenster-Aufteilung über die maximale Flügelbreite.
 *
 * Diese Größen sind das, was ein Schiebetor geometrisch von Dreh-/Flügeltoren unterscheidet,
 * und waren bisher nur indirekt (über lichteWeite/breite bei genau einer Breite) getestet.
 *
 * Die Tests prüfen vor allem die FORMEL-BEZIEHUNGEN (die per Definition gelten müssen) statt
 * fragiler Magic-Numbers, plus das im Quellcode dokumentierte Beispiel (torHöhe 1.5 / lichteWeite 4).
 */
export default {
  name: "TestSchiebetorKennzahlen",
};

// ---------------------------------------------------------------------------
// 1) Reine Kennzahlen-Formeln (aus Excel "Breitenberechnung Schiebetor 13-11-2023")
// ---------------------------------------------------------------------------
export const testKennzahlenFormelbeziehungen: Test = async () => {
  for (const lichteWeite of [2, 3, 4, 6]) {
    for (const torHöhe of [1.5, 1.8, 2.0]) {
      await subTestContext(`lichteWeite=${lichteWeite}/torHöhe=${torHöhe}`, async () => {
        const k = schiebetorKennzahlen({ lichteWeite, torHöhe });

        // feste Konstanten der Tabelle
        assert.equals(0.19, k.E, "E (190mm)");
        assert.equals(0.2, k.L5, "L5 (200mm)");
        assert.equals(0.08, k.rahmenBreite, "Rahmenbreite (80mm)");
        assert.equals(0.185, k.abstandKnickObenErsteStrebe, "Abstand Knick oben erste Strebe");

        // abgeleitete Größen
        assert.equals(lichteWeite, k.L1, "L1 == lichteWeite");
        assert.equals(lichteWeite * 0.3, k.L2, "L2 == L1 * 0.3");
        assert.equals(k.L2 + (k.E + k.L5) * 2, k.L4, "L4 == L2 + (E+L5)*2");
        assert.equals(k.L1 + k.L4, k.LT, "LT (Gesamt-Torrahmenbreite) == L1 + L4");
        assert.equals(k.LT, k.A, "A == LT");
        assert.equals(torHöhe + 0.01, k.B, "B == torHöhe + 10mm");
        assert.equals(lichteWeite - 0.04 + 0.185, k.C, "C == L1 - 40mm + 185mm");
        assert.equals(k.A - k.C, k.D, "D (knickDeltaX) == A - C");
        assert.equals(Math.atan((torHöhe - 0.13) / k.D), k.F, "F (Schrägwinkel) == atan((H-130mm)/D)");

        // Der Torrahmen ist breiter als die lichte Weite (Auslauf/Antriebsseite).
        assert.isTrue(k.LT > lichteWeite, "Torrahmen LT muss breiter als die lichte Weite sein");
      });
    }
  }
};

// ---------------------------------------------------------------------------
// 2) Dokumentiertes Referenzbeispiel (steht so als Kommentar in erzeugeSchiebetorRahmen.ts)
// ---------------------------------------------------------------------------
export const testKennzahlenReferenzbeispiel: Test = async () => {
  const k = schiebetorKennzahlen({ torHöhe: 1.5, lichteWeite: 4 });
  // Werte aus dem verifizierten Quellcode-Kommentar (erzeugeSchiebetorRahmen.ts)
  assert.equals(5.98, k.A, "A");
  assert.equals(1.51, k.B, "B");
  assert.equals(4.145, k.C, "C");
  assert.equals(1.835, k.D, "D");
  assert.equals(1.2, k.L2, "L2");
  assert.equals(1.98, k.L4, "L4");
  assert.equals(5.98, k.LT, "LT");
  // Im Quellcode-Kommentar ist 0.74655 das Verhältnis F1 = (H-130mm)/D, nicht der Winkel.
  assert.equals(0.74655, k.F1, "F1 (Verhältnis (H-130mm)/D)", 0.0005);
  assert.equals(Math.atan(0.74655), k.F, "F (Schrägwinkel rad) == atan(F1)", 0.0005);
};

// ---------------------------------------------------------------------------
// 3) schiebetorMasse spiegelt die Kennzahlen wider
// ---------------------------------------------------------------------------
export const testSchiebetorMasseKonsistent: Test = async () => {
  const input = { lichteWeite: 4, torHöhe: 1.8 };
  const k = schiebetorKennzahlen(input);
  const m = schiebetorMasse(input);
  assert.equals(k.LT, m.torBreite, "torBreite == LT");
  assert.equals(k.D, m.knickDeltaX, "knickDeltaX == D");
  assert.equals(k.rahmenBreite, m.rahmenBreite, "rahmenBreite");
  assert.equals(k.F, m.schrägwinkel, "schrägwinkel == F");
  assert.equals(k.abstandKnickObenErsteStrebe, m.abstandKnickObenErsteStrebe, "abstandKnickObenErsteStrebe");
  assert.equals(k.oeffnungsplatzbedarf, m.oeffnungsplatzbedarf, "oeffnungsplatzbedarf");
};

// ---------------------------------------------------------------------------
// 4) Öffnungsplatzbedarf hängt linear vom SchiebetorPuffer ab
// ---------------------------------------------------------------------------
export const testOeffnungsplatzbedarfFormel: Test = async () => {
  const input = { lichteWeite: 4, torHöhe: 1.8 };
  const k = schiebetorKennzahlen(input);
  const puffer = APP.settings.SchiebetorPuffer ?? 0.2;
  // oeffnungsplatzbedarf = lichteWeite + LT + Einzelpfosten(0.1) + Rahmenversatz(0.01) + Puffer
  assert.equals(
    input.lichteWeite + k.LT + 0.1 + 0.01 + puffer,
    k.oeffnungsplatzbedarf,
    "Öffnungsplatzbedarf-Formel"
  );
  // Der Platz zum Aufschieben ist größer als die lichte Weite des Tores.
  assert.isTrue(k.oeffnungsplatzbedarf > input.lichteWeite, "Öffnungsplatzbedarf > lichteWeite");
};

export const testSchiebetorPufferWirktAufOeffnungsplatz: Test = async () => {
  const input = { lichteWeite: 4, torHöhe: 1.8 };
  const orig = APP.settings.SchiebetorPuffer;
  try {
    APP.settings.SchiebetorPuffer = 0.2;
    const klein = schiebetorKennzahlen(input).oeffnungsplatzbedarf;
    APP.settings.SchiebetorPuffer = 0.7;
    const gross = schiebetorKennzahlen(input).oeffnungsplatzbedarf;
    // Differenz muss exakt der Puffer-Differenz entsprechen.
    assert.equals(0.5, gross - klein, "Öffnungsplatzbedarf wächst um die Puffer-Differenz");
  } finally {
    APP.settings.SchiebetorPuffer = orig;
  }
};

// ---------------------------------------------------------------------------
// 5) Fächer-/Füllungsfenster-Aufteilung über die maximale Flügelbreite
// ---------------------------------------------------------------------------
const fensterBreiten = (lichteWeite: number, torHöhe: number, schiebetorFlügelMaxBreite: number) =>
  erzeugeSchiebetorRahmen({
    torHöhe,
    lichteWeite,
    schiebetorFlügelMaxBreite,
    gespiegeltZ: false,
  } as any).füllungsFenster.map((b) => b.getSize(v2()).x);

export const testFüllungsfensterRespektierenMaxFlügelbreite: Test = async () => {
  const torHöhe = 1.8;
  const maxBreite = 1.0;
  for (const lichteWeite of [2, 3, 4, 6, 8]) {
    await subTestContext(`lichteWeite=${lichteWeite}`, async () => {
      const breiten = fensterBreiten(lichteWeite, torHöhe, maxBreite);
      assert.isTrue(breiten.length >= 1, "Es muss mindestens ein Füllungsfenster geben");
      breiten.forEach((b, i) =>
        assert.isTrue(
          b <= maxBreite + 0.0001,
          `Fenster ${i} (${b.toFixed(3)} m) darf die maximale Flügelbreite ${maxBreite} m nicht überschreiten`
        )
      );
    });
  }
};

export const testMehrFächerBeiKleinererMaxFlügelbreite: Test = async () => {
  const lichteWeite = 6;
  const torHöhe = 1.8;
  const wenig = fensterBreiten(lichteWeite, torHöhe, 1.86).length;
  const viele = fensterBreiten(lichteWeite, torHöhe, 0.6).length;
  assert.isTrue(
    viele > wenig,
    `Eine kleinere maximale Flügelbreite muss mehr Fächer ergeben (${viele} > ${wenig})`
  );
};

export const testMehrFächerBeiGrößererLichteWeite: Test = async () => {
  const torHöhe = 1.8;
  const maxBreite = 1.0;
  const schmal = fensterBreiten(2, torHöhe, maxBreite).length;
  const breit = fensterBreiten(8, torHöhe, maxBreite).length;
  assert.isTrue(breit > schmal, `Ein breiteres Tor muss mehr Fächer haben (${breit} > ${schmal})`);
};

// ---------------------------------------------------------------------------
// 6) Der RahmenDef des Tores ist konsistent mit den reinen Berechnungsfunktionen
// ---------------------------------------------------------------------------
export const testRahmenDefKonsistentMitMasse: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [10]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  await agt.changeSelectedZaunElemente({
    massAuswahlwert: APP.richMetadata.findAuswahlwert("Schiebetor_Easy"),
  });
  agt.update3d();
  const tor = agt.getTore()[0];
  assert.isTrue(tor.isSchiebetor, "ist Schiebetor");
  assertWarnungen(agt, []);

  const def = tor.schiebetorRahmenDef;
  assert.isNotNull(def, "schiebetorRahmenDef muss gesetzt sein");
  // Die lichte Weite des Tores entspricht der lichten Weite im RahmenDef.
  assert.equals(tor.lichteWeite, def.lichteWeite, "RahmenDef.lichteWeite == tor.lichteWeite");
  // Der Torrahmen (mit Auslauf) ist breiter als die Stellfläche des Tores.
  assert.isTrue(def.torBreite > tor.breite, "RahmenDef.torBreite (mit Auslauf) > tor.breite");
  assert.isTrue(def.dreiecksBreite > 0, "Es gibt einen Dreiecks-/Auslaufbereich");
  // Konsistenz zur reinen Berechnung (mit der im RahmenDef gespeicherten lichten Weite,
  // damit der Input garantiert identisch zum Erzeugungszeitpunkt ist).
  const m = schiebetorMasse({ lichteWeite: def.lichteWeite, torHöhe: tor.höhe });
  assert.equals(m.torBreite, def.torBreite, "torBreite konsistent mit schiebetorMasse");
  assert.equals(m.oeffnungsplatzbedarf, def.oeffnungsplatzbedarf, "oeffnungsplatzbedarf konsistent");
  assert.isTrue(def.füllungsFenster.length >= 1, "Mindestens ein Füllungsfenster im RahmenDef");
};
